/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import {
  VolumeTimeSeriesOptions,
  VolumeTimeSeriesService,
} from 'src/app/shared/services/data-api/volume-time-series.service';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';
import { QueryParams } from 'src/app/shared/models/query-params.interface';

/**
 * Volume / query-sentiment fetches for the news chart. Bug-for-bug replacement
 * for the chart usages of `yukkApi.scoresTimeSeries('volume'|'sentiment', ...)`:
 * same iRequest context dispatch (market/folio/query/search) and the same
 * params (`newsday` ignored, no `period` on volume_ts).
 */
@Injectable({ providedIn: 'root' })
export class NewsChartDataService {
  constructor(
    private routing: RoutingService,
    private portfolioApi: PortfolioApiService,
    private queryApi: QueryApiService,
    private volumeTimeSeries: VolumeTimeSeriesService,
    private sentimentService: SentimentService,
  ) {}

  fetchVolume(
    qparam: QueryParams,
    options: VolumeTimeSeriesOptions = {},
  ): Observable<any> {
    // Legacy myParams() ignored `newsday` — strip it so ParamsBuilder cannot
    // narrow the range to a single day.
    const params = { ...qparam };
    delete params.newsday;

    if (this.routing.isMarket() || params.portfolioId) {
      if (params.portfolio) {
        if (this.portfolioApi.portfolios) {
          return this.portfolioApi.portfolios
            .filter((p: any) => p.uid === params.portfolioId)
            .map((item: any) =>
              this.volumeTimeSeries
                .getPortfolioVolumeTimeSeries(item.content, params, options)
                .pipe(catchError(() => of(undefined))),
            )[0];
        }
        return of({});
      }
      if (this.portfolioApi.activePortfolio?.marketPortfolio) {
        return this.volumeTimeSeries
          .getPortfolioVolumeTimeSeries(
            this.portfolioApi.activePortfolio.content,
            params,
            options,
          )
          .pipe(catchError(() => of(undefined)));
      }
      return this.volumeTimeSeries.getEntityVolumeTimeSeries(
        params.type,
        params.id,
        params,
        options,
      );
    }

    if (this.routing.isFolio() && !params.portfolioId) {
      return this.volumeTimeSeries.getPortfolioVolumeTimeSeries(
        this.portfolioApi.activePortfolio
          ? this.portfolioApi.activePortfolio.content
          : [],
        params,
        options,
      );
    }

    if (this.routing.isQuery() || this.routing.isSearch()) {
      return this.resolveQueryPayload(params).pipe(
        switchMap((payload) =>
          this.volumeTimeSeries.getQueryVolumeTimeSeries(
            payload,
            params,
            options,
          ),
        ),
      );
    }

    return this.volumeTimeSeries.getEntityVolumeTimeSeries(
      params.type,
      params.id,
      params,
      options,
    );
  }

  /**
   * Rolling sentiment line for the query/search chart — legacy v1
   * `search_query/quotient_ts` (no v2 score_ts equivalent for query payloads).
   */
  fetchQuerySentiment(
    qparam: QueryParams,
    period: number,
  ): Observable<{ quotient_ts: { date: string; quotient: number }[] }> {
    return this.resolveQueryPayload(qparam).pipe(
      switchMap((payload) =>
        this.sentimentService.getQuerySentimentTimeSeries(payload, {
          ...qparam,
          period,
        }),
      ),
    );
  }

  // Legacy iRequest query routing: raw query ONLY for isSearch(); single query
  // in a collection → resolveQuery; collection/group view → resolveQueryGroup.
  private resolveQueryPayload(qparam: QueryParams): Observable<unknown> {
    if (this.routing.isSearch()) {
      return of(this.queryApi.query.query);
    }
    if (this.queryApi.query && qparam.groupId !== qparam.id) {
      return this.queryApi
        .resolveQuery(this.queryApi.query.uid)
        .pipe(map((response: any) => response.query));
    }
    return this.queryApi.resolveQueryGroup(this.queryApi.query.uid).pipe(
      map((response: any) => ({
        operator: 'or',
        filters: [],
        groups: response.content.map((item: any) => item.query),
      })),
    );
  }
}
